import React, { useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import LandingPage from './components/landing/LandingPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import SystemAdminRoute from './components/auth/SystemAdminRoute';
import PermissionRoute from './components/auth/PermissionRoute';
import UserList from './components/users/UserList';
import InstitutionList from './components/institutions/InstitutionList';
import InstitutionDetail from './components/institutions/InstitutionDetail';
import CreateInstitution from './components/institutions/CreateInstitution';
import InstitutionSettingsPage from './components/institutions/InstitutionSettingsPage';
import TenantProvisioning from './components/institutions/TenantProvisioning';
import AccountingOverview from './components/accounting/AccountingOverview';
import JournalEntryList from './components/accounting/JournalEntryList';
import JournalEntryForm from './components/accounting/JournalEntryForm';
import JournalEntryDetail from './components/accounting/JournalEntryDetail';
import ChartOfAccounts from './components/accounting/ChartOfAccounts';
import AccountingReports from './components/accounting/AccountingReports';
import OpeningBalances from './components/accounting/OpeningBalances';
import MonthEndClose from './components/accounting/MonthEndClose';
import Sidebar from './components/shared/Sidebar';
import { DashboardAccessProvider } from './components/dashboard/useDashboardAccess';
import { PlanProvider } from './context/PlanContext';
import MainHeader from './components/shared/MainHeader';
import { AuthService } from './services/auth.service';
import AttendanceList from './components/attendance/AttendanceList';
import EmployeeAttendanceList from './components/attendance/EmployeeAttendanceList';
import InstitutionTodayAttendance from './components/attendance/InstitutionTodayAttendance';
import AttendanceStatusUserList from './components/attendance/AttendanceStatusUserList';
import HolidayList from './components/holiday/HolidayList';
import HolidayForm from './components/holiday/HolidayForm';
import ShiftListPage from './components/shifts/ShiftListPage';
import ShiftAssignmentPage from './components/shifts/ShiftAssignmentPage';
import RotationPatternListPage from './components/shifts/RotationPatternListPage';
import ShiftRosterCalendar from './components/shifts/ShiftRosterCalendar';
import OrphanPunchInbox from './components/shifts/OrphanPunchInbox';
import LeaveApplicationList from './components/leave/LeaveApplicationList';
import AllLeaveApplications from './components/leave/AllLeaveApplications';
import LeavePolicyManagement from './components/leave/LeavePolicyManagement';
import LeaveTypeManagement from './components/leave/LeaveTypeManagement';
import UserLeaveAllocationManagement from './components/leave/UserLeaveAllocationManagement';
import AllUserLeaveBalances from './components/leave/AllUserLeaveBalances';
import './App.css';
import UserCreate from './components/users/UserCreate';
import AllInstitutionUsers from './components/users/AllInstitutionUsers';
import UserProfile from './components/users/UserProfile';
import { PERMISSIONS } from './constants/permissions';
import { UserType } from './interfaces/userType.enum';
import Dashboard from './components/dashboard/Dashboard';
import ModuleBoard from './components/dashboard/board/ModuleBoard';
import KpiTargetsSettings from './components/dashboard/pages/KpiTargetsSettings';
import { MODULE_DEFAULT_PRESETS } from './components/dashboard/registry/presets';
import DeviceUserList from './components/device-user/DeviceUserList';
import DeviceApiKeyList from './components/device-api-key/DeviceApiKeyList';
import SalaryComponentList from './components/payroll/SalaryComponentList';
import EmployeeSalaryList from './components/payroll/EmployeeSalaryList';
import PayrollSettings from './components/payroll/PayrollSettings';
import PayrollList from './components/payroll/PayrollList';
import PayrollDetail from './components/payroll/PayrollDetail';
import PaymentReconciliation from './components/payroll/PaymentReconciliation';
import ImpersonationBanner from './components/shared/ImpersonationBanner';
import UserPermissions from './components/users/UserPermissions';
import EmployeeDocumentAdmin from './components/users/EmployeeDocumentAdmin';
import DepartmentList from './components/department/DepartmentList';
import DepartmentForm from './components/department/DepartmentForm';
import DepartmentUserAssignment from './components/department/DepartmentUserAssignment';
import NoticeFeed from './components/notice/NoticeFeed';
import NoticeAdminList from './components/notice/NoticeAdminList';
import MyTasks from './components/task/MyTasks';
import TasksAssignedByMe from './components/task/TasksAssignedByMe';
import TaskAdminList from './components/task/TaskAdminList';
import MyAppraisals from './components/appraisal/MyAppraisals';
import ReviewsToWrite from './components/appraisal/ReviewsToWrite';
import ReviewPage from './components/appraisal/ReviewPage';
import CycleList from './components/appraisal/CycleList';
import CycleEditor from './components/appraisal/CycleEditor';
import TaxSettings from './components/tax/TaxSettings';
import TaxSlabList from './components/tax/TaxSlabList';
import TaxExemptionList from './components/tax/TaxExemptionList';
import EmployeeTaxList from './components/tax/EmployeeTaxList';
import TaxReports from './components/tax/TaxReports';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { bnBD, enUS } from '@mui/material/locale';
import { useTranslation } from 'react-i18next';
import appTheme from './theme/appTheme';
import BillingPage from './components/billing/BillingPage';
import OperatorBillingPage from './components/billing/OperatorBillingPage';
import RoleLanding from './components/auth/RoleLanding';
import { LANDING_ROUTE, postLoginPath } from './utils/roleLanding';

// Component to handle login route logic
const LoginRoute = () => {
  const location = useLocation();
  // A signed-in visit to /login goes to a safe deep link, else to the role landing (/home).
  const from = postLoginPath(location.state, location.search);

  if (AuthService.isValidToken()) {
    return <Navigate to={from} replace />;
  }

  return <LandingPage />;
};

// MUI's own texts (pagination, "Rows per page") follow the app language.
const MUI_LOCALES: Record<string, typeof enUS> = { en: enUS, bn: bnBD };

function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const theme = useMemo(() => createTheme(appTheme, MUI_LOCALES[language] ?? enUS), [language]);

  return (
    <ThemeProvider theme={theme}>
    <AuthProvider>
      <ToastProvider>
      <Router>
        <div className="app">
          <MainHeader />
          <Routes>
            <Route path="/login" element={<LoginRoute />} />

            {/* Protected Routes with Sidebar */}
            <Route element={<ProtectedRoute />}>
              <Route
                path="/*"
                element={
                  <DashboardAccessProvider>
                  <PlanProvider>
                  <div className="app-container">
                    <ImpersonationBanner />
                    <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(prev => !prev)} />
                    <div
                      className="content-container"
                      style={{
                        marginLeft: sidebarCollapsed ? '60px' : '250px',
                        width: sidebarCollapsed ? 'calc(100% - 60px)' : 'calc(100% - 250px)',
                      }}
                    >
                      <main className="main-content">
                        <Routes>
                          {/* Institution Admin Only Routes */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MENU_USERS} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/users" element={<UserList />} />
                            <Route path="/users/create" element={<UserCreate />} />
                            <Route path="/users/:userId/permissions" element={<UserPermissions />} />
                            <Route path="/employee-documents" element={<EmployeeDocumentAdmin />} />
                            <Route path="/holidays/create" element={<HolidayForm />} />
                            <Route path="/holidays/edit/:id" element={<HolidayForm />} />
                            <Route path="/attendance/employee" element={<EmployeeAttendanceList />} />
                            <Route path="/attendance/today" element={<InstitutionTodayAttendance />} />
                            <Route path="/attendance/status/:status" element={<AttendanceStatusUserList />} />
                            <Route path="/device-users" element={<DeviceUserList />} />
                            <Route path="/device-api-keys" element={<DeviceApiKeyList />} />
                          </Route>

                          {/* Department Routes — list + user assignment (InstitutionAdmin OR department admin) */}
                          <Route element={<PermissionRoute allowedRoles={[UserType.InstitutionAdmin]} alsoAllowIf={u => u?.isDepartmentAdmin === true} />}>
                            <Route path="/departments" element={<DepartmentList />} />
                            <Route path="/departments/:departmentId/users" element={<DepartmentUserAssignment />} />
                          </Route>

                          {/* Department CRUD — InstitutionAdmin only */}
                          <Route element={<PermissionRoute allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/departments/create" element={<DepartmentForm />} />
                            <Route path="/departments/edit/:id" element={<DepartmentForm />} />
                          </Route>

                          {/* Billing — InstitutionAdmin only */}
                          <Route element={<PermissionRoute allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/billing" element={<BillingPage />} />
                          </Route>

                          {/* Operator Billing console — SaaSOperator + SystemAdmin */}
                          <Route element={<PermissionRoute allowedRoles={[UserType.SystemAdmin, UserType.SaaSOperator]} />}>
                            <Route path="/operator/billing" element={<OperatorBillingPage />} />
                          </Route>

                          {/* Leave sub-menu routes — each guarded by its own sub-menu permission */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_LEAVE_ALL_APPLICATIONS} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/all-leave-applications" element={<AllLeaveApplications />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_LEAVE_TYPES} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/leave-type" element={<LeaveTypeManagement />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_LEAVE_POLICY} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/leave-policy" element={<LeavePolicyManagement />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_LEAVE_ALLOCATIONS} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/user-leave-allocations" element={<UserLeaveAllocationManagement />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_LEAVE_BALANCES} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/all-user-leave-balances" element={<AllUserLeaveBalances />} />
                          </Route>

                          {/* Shifts — Institution Admin (gated by sub-menu permission) */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_SHIFT_LIST} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/shifts" element={<ShiftListPage />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_SHIFT_ASSIGNMENTS} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/shifts/assignments" element={<ShiftAssignmentPage />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_SHIFT_ROTATION_PATTERNS} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/shifts/rotation-patterns" element={<RotationPatternListPage />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_SHIFT_ROSTER} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/shifts/roster" element={<ShiftRosterCalendar />} />
                          </Route>
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.SUBMENU_SHIFT_ORPHANS} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/shifts/orphan-punches" element={<OrphanPunchInbox />} />
                          </Route>

                          {/* Payroll Routes - Institution Admin and System Admin */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MENU_PAYROLL} allowedRoles={[UserType.InstitutionAdmin, UserType.SystemAdmin]} />}>
                            <Route path="/payroll/salary-components" element={<SalaryComponentList />} />
                            <Route path="/payroll/employee-salaries" element={<EmployeeSalaryList />} />
                            <Route path="/payroll/settings" element={<PayrollSettings />} />
                            <Route path="/payroll/run" element={<PayrollList />} />
                            <Route path="/payroll/run/:id" element={<PayrollDetail />} />
                            <Route path="/payroll/reconciliation" element={<PaymentReconciliation />} />
                          </Route>

                          {/* Tax Module Routes - Institution Admin and Accountant */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MENU_TAX} allowedRoles={[UserType.InstitutionAdmin, UserType.Accountant]} />}>
                            <Route path="/tax/settings" element={<TaxSettings />} />
                            <Route path="/tax/slabs" element={<TaxSlabList />} />
                            <Route path="/tax/exemptions" element={<TaxExemptionList />} />
                            <Route path="/tax/employees" element={<EmployeeTaxList />} />
                            <Route path="/tax/reports" element={<TaxReports />} />
                          </Route>

                          {/* System Admin Only Routes */}
                          <Route element={<SystemAdminRoute />}>
                            <Route path="/institutions" element={<InstitutionList />} />
                            <Route path="/institutions/create" element={<CreateInstitution />} />
                            <Route path="/institutions/:id" element={<InstitutionDetail />} />
                            <Route path="/all-institution-users" element={<AllInstitutionUsers />} />
                          </Route>

                          {/* SaaS Operator console — premium dashboard provisioning across institutions */}
                          <Route element={<PermissionRoute allowedRoles={[UserType.SaaSOperator, UserType.SystemAdmin]} />}>
                            <Route path="/saas/premium-provisioning" element={<TenantProvisioning />} />
                          </Route>

                          {/* Institution Admin can manage their own institution's settings (e.g. weekend days) */}
                          <Route element={<PermissionRoute allowedRoles={[UserType.InstitutionAdmin, UserType.SystemAdmin]} />}>
                            <Route path="/institution-settings" element={<InstitutionSettingsPage />} />
                          </Route>

                          {/* Other existing routes */}
                          <Route path="/attendance" element={<AttendanceList />} />
                          <Route path="/holidays" element={<HolidayList />} />
                          <Route path="/leave-applications" element={<LeaveApplicationList />} />

                          {/* User Profile Route - Available for all authenticated users */}
                          <Route path="/profile" element={<UserProfile />} />

                          {/* Accounting Routes - Accountant and Institution Admin */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MENU_ACCOUNTING} allowedRoles={[UserType.Accountant, UserType.InstitutionAdmin]} />}>
                            <Route path="/accounting" element={<AccountingOverview />} />
                            <Route path="/accounting/entries" element={<JournalEntryList />} />
                            <Route path="/accounting/entries/new" element={<JournalEntryForm />} />
                            <Route path="/accounting/entries/:id" element={<JournalEntryDetail />} />
                            <Route path="/accounting/accounts" element={<ChartOfAccounts />} />
                            <Route path="/accounting/reports" element={<AccountingReports />} />
                            <Route path="/accounting/opening-balances" element={<OpeningBalances />} />
                            <Route path="/accounting/closing" element={<MonthEndClose />} />
                          </Route>

                          {/* Fixed Assets */}
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MENU_ACCOUNTING} allowedRoles={[UserType.Accountant, UserType.InstitutionAdmin, UserType.InstitutionManager]} />}>
                          </Route>

                          {/* Notices */}
                          <Route path="/notices" element={<NoticeFeed />} />
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.ACTION_MANAGE_NOTICES} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/admin/notices" element={<NoticeAdminList />} />
                          </Route>

                          {/* Tasks */}
                          <Route path="/tasks" element={<MyTasks />} />
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.ACTION_ASSIGN_TASK} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/tasks/assigned-by-me" element={<TasksAssignedByMe />} />
                            <Route path="/admin/tasks" element={<TaskAdminList />} />
                          </Route>

                          {/* Appraisals: everyone sees their own and the ones they review; cycles are for admins */}
                          <Route path="/appraisals/mine" element={<MyAppraisals />} />
                          <Route path="/appraisals/to-write" element={<ReviewsToWrite />} />
                          <Route path="/appraisals/reviews/:reviewId" element={<ReviewPage />} />
                          <Route element={<PermissionRoute permissionKey={PERMISSIONS.MENU_APPRAISAL} allowedRoles={[UserType.InstitutionAdmin]} />}>
                            <Route path="/appraisals/cycles" element={<CycleList />} />
                            <Route path="/appraisals/cycles/:cycleId" element={<CycleEditor />} />
                          </Route>

                          {/* Expense Routes */}

                          {/* Dashboard module deep-pages — reached from the Dashboard submenu */}
                          <Route element={<PermissionRoute allowedRoles={[UserType.InstitutionAdmin, UserType.InstitutionManager, UserType.Accountant]} />}>
                            <Route path="/dashboard/attendance" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="attendance" moduleScope="attendance" fallbackIds={MODULE_DEFAULT_PRESETS.attendance} />} />
                            <Route path="/dashboard/leave" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="leave" moduleScope="leave" fallbackIds={MODULE_DEFAULT_PRESETS.leave} />} />
                            <Route path="/dashboard/people" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="people" moduleScope="people" fallbackIds={MODULE_DEFAULT_PRESETS.people} />} />
                            <Route path="/dashboard/payroll" element={<ModuleBoard institutionId={AuthService.getSelectedInstitution()} boardKey="payroll" moduleScope="payroll" fallbackIds={MODULE_DEFAULT_PRESETS.payroll} />} />
                          </Route>

                          <Route element={<PermissionRoute allowedRoles={[UserType.InstitutionAdmin, UserType.InstitutionManager]} />}>
                            <Route path="/dashboard/settings/targets" element={<KpiTargetsSettings />} />
                          </Route>

                          <Route path={LANDING_ROUTE} element={<RoleLanding />} />
                          <Route path="/" element={<Dashboard />} />
                        </Routes>
                      </main>
                    </div>
                  </div>
                  </PlanProvider>
                  </DashboardAccessProvider>
                }
              />
            </Route>
          </Routes>
        </div>
      </Router>
      </ToastProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
